<template>
  <NavBar pageName="Goods Inventory"></NavBar>
  <v-container style="margin-top: 185px;" fluid>
    <v-btn variant="text" size="small" @click="$router.push('/settings/goods')" class="mb-2">
      <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
      Goods Settings
    </v-btn>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-2">
        <span>Goods Inventory</span>
        <v-btn color="success" size="small" @click="openAddDialog">
          <v-icon class="mr-1">mdi-plus</v-icon>
          Add Item
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Filters -->
        <v-row dense class="mb-4">
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.good_category_id"
              :items="categories"
              item-title="name"
              item-value="id"
              label="Category"
              variant="outlined"
              density="compact"
              clearable
              @update:model-value="loadInventories"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.inventory_status_id"
              :items="statuses"
              item-title="name"
              item-value="id"
              label="Status"
              variant="outlined"
              density="compact"
              clearable
              @update:model-value="loadInventories"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="filters.location_client_id"
              :items="clients"
              item-title="name"
              item-value="id"
              label="Location (Client)"
              variant="outlined"
              density="compact"
              clearable
              @update:model-value="loadInventories"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.search"
              label="Search (ID, Tracking #)"
              variant="outlined"
              density="compact"
              clearable
              append-inner-icon="mdi-magnify"
              @keyup.enter="loadInventories"
              @click:clear="loadInventories"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-data-table-server
          :headers="headers"
          :items="inventories"
          :loading="loading"
          :items-length="pagination.total_items"
          :items-per-page="pagination.per_page"
          :page="pagination.current_page"
          @update:page="onPageChange"
          @update:items-per-page="onPerPageChange"
          class="elevation-1"
        >
          <template v-slot:[`item.good_id`]="{ item }">
            <div>
              <span class="font-weight-medium">{{ item.good_id }}</span>
              <!-- SIM Details when available -->
              <div v-if="item.sim_details" class="text-caption text-grey">
                <div v-if="item.sim_details.imsi">IMSI: {{ item.sim_details.imsi }}</div>
                <div v-if="item.sim_details.msisdn">MSISDN: {{ item.sim_details.msisdn }}</div>
                <div v-if="item.sim_details.serial">Serial: {{ item.sim_details.serial }}</div>
              </div>
            </div>
          </template>

          <template v-slot:[`item.inventory_status_name`]="{ item }">
            <v-chip
              :color="getStatusColor(item.inventory_status_name)"
              size="small"
            >
              {{ item.inventory_status_name }}
            </v-chip>
          </template>

          <template v-slot:[`item.shipping_date`]="{ item }">
            {{ formatDate(item.shipping_date) }}
          </template>

          <template v-slot:[`item.received_date`]="{ item }">
            {{ formatDate(item.received_date) }}
          </template>

          <template v-slot:[`item.updated_at`]="{ item }">
            {{ formatDateTime(item.updated_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              icon
              size="small"
              variant="text"
              color="info"
              @click="viewLogs(item)"
              title="View History"
            >
              <v-icon>mdi-history</v-icon>
            </v-btn>
            <v-btn
              icon
              size="small"
              variant="text"
              color="warning"
              @click="openStatusDialog(item)"
              title="Update Status"
            >
              <v-icon>mdi-swap-horizontal</v-icon>
            </v-btn>
            <v-btn
              icon
              size="small"
              variant="text"
              color="primary"
              @click="editInventory(item)"
              title="Edit"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              size="small"
              variant="text"
              color="error"
              @click="confirmDelete(item)"
              title="Delete"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table-server>
      </v-card-text>
    </v-card>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="showDialog" max-width="700px" persistent>
      <v-card>
        <v-card-title>
          {{ editingId ? 'Edit' : 'Add' }} Inventory Item
        </v-card-title>

        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.good_id"
                label="Good ID (e.g., IMSI number)"
                variant="outlined"
                density="compact"
                :rules="[v => !!v || 'Good ID is required']"
                :disabled="!!editingId"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="formData.good_category_id"
                :items="categories"
                item-title="name"
                item-value="id"
                label="Category"
                variant="outlined"
                density="compact"
                :rules="[v => !!v || 'Category is required']"
                :disabled="!!editingId"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="formData.inventory_status_id"
                :items="statuses"
                item-title="name"
                item-value="id"
                label="Status"
                variant="outlined"
                density="compact"
                :rules="[v => !!v || 'Status is required']"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="formData.location_client_id"
                :items="clients"
                item-title="name"
                item-value="id"
                label="Location (Client)"
                variant="outlined"
                density="compact"
                clearable
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.shipping_date"
                label="Shipping Date"
                variant="outlined"
                density="compact"
                type="date"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.tracking_number"
                label="Tracking Number"
                variant="outlined"
                density="compact"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.expected_delivery_date"
                label="Expected Delivery"
                variant="outlined"
                density="compact"
                type="date"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.received_date"
                label="Received Date"
                variant="outlined"
                density="compact"
                type="date"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="8">
              <v-textarea
                v-model="formData.notes"
                label="Notes"
                variant="outlined"
                density="compact"
                rows="2"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="saveInventory"
            :loading="saving"
            :disabled="!formData.good_id || !formData.good_category_id || !formData.inventory_status_id"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Update Status Dialog -->
    <v-dialog v-model="showStatusDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>Update Inventory Status</v-card-title>

        <v-card-text>
          <v-alert type="info" density="compact" class="mb-4">
            Current: {{ statusFormData.current_status }} at {{ statusFormData.current_location || 'N/A' }}
          </v-alert>

          <v-select
            v-model="statusFormData.inventory_status_id"
            :items="statuses"
            item-title="name"
            item-value="id"
            label="New Status"
            variant="outlined"
            density="compact"
            class="mb-3"
          ></v-select>

          <v-autocomplete
            v-model="statusFormData.location_client_id"
            :items="clients"
            item-title="name"
            item-value="id"
            label="New Location (Client)"
            variant="outlined"
            density="compact"
            clearable
            class="mb-3"
          ></v-autocomplete>

          <v-row dense>
            <v-col cols="6">
              <v-text-field
                v-model="statusFormData.shipping_date"
                label="Shipping Date"
                variant="outlined"
                density="compact"
                type="date"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="statusFormData.tracking_number"
                label="Tracking Number"
                variant="outlined"
                density="compact"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-text-field
            v-model="statusFormData.received_date"
            label="Received Date"
            variant="outlined"
            density="compact"
            type="date"
            class="mb-3"
          ></v-text-field>

          <v-textarea
            v-model="statusFormData.notes"
            label="Notes"
            variant="outlined"
            density="compact"
            rows="2"
          ></v-textarea>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showStatusDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="updateStatus"
            :loading="updating"
            :disabled="!statusFormData.inventory_status_id"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Logs Dialog -->
    <v-dialog v-model="showLogsDialog" max-width="900px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Inventory History - {{ logsFormData.good_id }}</span>
          <v-btn icon size="small" variant="text" @click="showLogsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-data-table
            :headers="logHeaders"
            :items="logs"
            :loading="loadingLogs"
            :items-per-page="10"
            class="elevation-1"
          >
            <template v-slot:[`item.from_inventory_status_name`]="{ item }">
              <v-chip v-if="item.from_inventory_status_name" size="small" :color="getStatusColor(item.from_inventory_status_name)">
                {{ item.from_inventory_status_name }}
              </v-chip>
              <span v-else>-</span>
            </template>

            <template v-slot:[`item.to_inventory_status_name`]="{ item }">
              <v-chip size="small" :color="getStatusColor(item.to_inventory_status_name)">
                {{ item.to_inventory_status_name }}
              </v-chip>
            </template>

            <template v-slot:[`item.created_at`]="{ item }">
              {{ formatDateTime(item.created_at) }}
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete this inventory record? This will also delete all history logs.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="deleteInventory" :loading="deleting">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import store from "@/store";
import { log } from "@/composables/useLog";
import {
  getGoodCategories,
  getInventoryStatuses,
  getGoodsInventories,
  saveGoodsInventory,
  deleteGoodsInventory as deleteGoodsInventoryAPI,
  updateGoodsInventoryStatus,
  getGoodsInventoryLogs
} from "@/composables/useGoodsInventoryAPI";
import { getClientsList } from "@/composables/useGetClientsListAPI";

export default defineComponent({
  name: 'GoodsInventoryList',
  components: {
    NavBar
  },
  data() {
    return {
      loading: false,
      saving: false,
      updating: false,
      deleting: false,
      loadingLogs: false,
      inventories: [],
      categories: [],
      statuses: [],
      clients: [],
      logs: [],
      filters: {
        good_category_id: null,
        inventory_status_id: null,
        location_client_id: null,
        search: ''
      },
      pagination: {
        current_page: 1,
        per_page: 25,
        total_items: 0,
        total_pages: 0
      },
      showDialog: false,
      showStatusDialog: false,
      showLogsDialog: false,
      showDeleteDialog: false,
      editingId: null,
      deleteId: null,
      formData: {
        good_id: '',
        good_category_id: null,
        inventory_status_id: null,
        location_client_id: null,
        shipping_date: '',
        tracking_number: '',
        expected_delivery_date: '',
        received_date: '',
        notes: ''
      },
      statusFormData: {
        id: null,
        current_status: '',
        current_location: '',
        inventory_status_id: null,
        location_client_id: null,
        shipping_date: '',
        tracking_number: '',
        received_date: '',
        notes: ''
      },
      logsFormData: {
        good_id: ''
      },
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success'
    }
  },
  computed: {
    token() {
      return store.getters.getToken;
    },
    headers() {
      return [
        { title: 'Good ID', key: 'good_id', width: '180px' },
        { title: 'Category', key: 'good_category_name', width: '120px' },
        { title: 'Status', key: 'inventory_status_name', width: '140px' },
        { title: 'Location', key: 'location_client_name' },
        { title: 'Tracking #', key: 'tracking_number', width: '150px' },
        { title: 'Shipped', key: 'shipping_date', width: '110px' },
        { title: 'Received', key: 'received_date', width: '110px' },
        { title: 'Updated', key: 'updated_at', width: '160px' },
        { title: 'Actions', key: 'actions', sortable: false, width: '180px' }
      ];
    },
    logHeaders() {
      return [
        { title: 'Date', key: 'created_at', width: '160px' },
        { title: 'From Status', key: 'from_inventory_status_name', width: '130px' },
        { title: 'To Status', key: 'to_inventory_status_name', width: '130px' },
        { title: 'From Location', key: 'from_client_name' },
        { title: 'To Location', key: 'to_client_name' },
        { title: 'Tracking #', key: 'tracking_number', width: '120px' },
        { title: 'Notes', key: 'notes' },
        { title: 'By', key: 'created_by_user_name', width: '120px' }
      ];
    }
  },
  methods: {
    async loadData() {
      await Promise.all([
        this.loadCategories(),
        this.loadStatuses(),
        this.loadClients(),
        this.loadInventories()
      ]);
    },

    async loadCategories() {
      try {
        const response = await getGoodCategories(this.token);
        if (!response.error) {
          this.categories = response.good_categories || [];
        }
      } catch (err) {
        log("Error loading categories:", err);
      }
    },

    async loadStatuses() {
      try {
        const response = await getInventoryStatuses(this.token);
        if (!response.error) {
          this.statuses = response.inventory_statuses || [];
        }
      } catch (err) {
        log("Error loading statuses:", err);
      }
    },

    async loadClients() {
      try {
        const response = await getClientsList(this.token, {});
        if (!response.error) {
          this.clients = response.clients || [];
        }
      } catch (err) {
        log("Error loading clients:", err);
      }
    },

    async loadInventories() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
          ...this.filters
        };

        const response = await getGoodsInventories(this.token, params);
        if (!response.error) {
          this.inventories = response.goods_inventories || [];
          this.pagination = response.pagination || this.pagination;
        } else {
          this.showMessage(response.message || 'Error loading inventories', 'error');
        }
      } catch (err) {
        log("Error loading inventories:", err);
        this.showMessage('Error loading inventories', 'error');
      } finally {
        this.loading = false;
      }
    },

    onPageChange(page) {
      this.pagination.current_page = page;
      this.loadInventories();
    },

    onPerPageChange(perPage) {
      this.pagination.per_page = perPage;
      this.pagination.current_page = 1;
      this.loadInventories();
    },

    openAddDialog() {
      this.editingId = null;
      this.formData = {
        good_id: '',
        good_category_id: this.categories.length === 1 ? this.categories[0].id : null,
        inventory_status_id: null,
        location_client_id: null,
        shipping_date: '',
        tracking_number: '',
        expected_delivery_date: '',
        received_date: '',
        notes: ''
      };
      this.showDialog = true;
    },

    editInventory(item) {
      this.editingId = item.id;
      this.formData = {
        good_id: item.good_id,
        good_category_id: item.good_category_id,
        inventory_status_id: item.inventory_status_id,
        location_client_id: item.location_client_id,
        shipping_date: item.shipping_date || '',
        tracking_number: item.tracking_number || '',
        expected_delivery_date: item.expected_delivery_date || '',
        received_date: item.received_date || '',
        notes: item.notes || ''
      };
      this.showDialog = true;
    },

    closeDialog() {
      this.showDialog = false;
      this.editingId = null;
    },

    async saveInventory() {
      this.saving = true;
      try {
        const data = {
          id: this.editingId,
          ...this.formData
        };

        const response = await saveGoodsInventory(this.token, data);
        if (!response.error) {
          this.showMessage(response.message || 'Inventory saved', 'success');
          this.closeDialog();
          await this.loadInventories();
        } else {
          this.showMessage(response.message || 'Error saving inventory', 'error');
        }
      } catch (err) {
        log("Error saving inventory:", err);
        this.showMessage('Error saving inventory', 'error');
      } finally {
        this.saving = false;
      }
    },

    openStatusDialog(item) {
      this.statusFormData = {
        id: item.id,
        current_status: item.inventory_status_name,
        current_location: item.location_client_name,
        inventory_status_id: item.inventory_status_id,
        location_client_id: item.location_client_id,
        shipping_date: '',
        tracking_number: '',
        received_date: '',
        notes: ''
      };
      this.showStatusDialog = true;
    },

    async updateStatus() {
      this.updating = true;
      try {
        const data = {
          id: this.statusFormData.id,
          inventory_status_id: this.statusFormData.inventory_status_id,
          location_client_id: this.statusFormData.location_client_id,
          shipping_date: this.statusFormData.shipping_date,
          tracking_number: this.statusFormData.tracking_number,
          received_date: this.statusFormData.received_date,
          notes: this.statusFormData.notes
        };

        const response = await updateGoodsInventoryStatus(this.token, data);
        if (!response.error) {
          this.showMessage(response.message || 'Status updated', 'success');
          this.showStatusDialog = false;
          await this.loadInventories();
        } else {
          this.showMessage(response.message || 'Error updating status', 'error');
        }
      } catch (err) {
        log("Error updating status:", err);
        this.showMessage('Error updating status', 'error');
      } finally {
        this.updating = false;
      }
    },

    async viewLogs(item) {
      this.logsFormData.good_id = item.good_id;
      this.showLogsDialog = true;
      this.loadingLogs = true;

      try {
        const response = await getGoodsInventoryLogs(this.token, { inventory_id: item.id });
        if (!response.error) {
          this.logs = response.goods_inventory_logs || [];
        } else {
          this.showMessage(response.message || 'Error loading logs', 'error');
        }
      } catch (err) {
        log("Error loading logs:", err);
        this.showMessage('Error loading logs', 'error');
      } finally {
        this.loadingLogs = false;
      }
    },

    confirmDelete(item) {
      this.deleteId = item.id;
      this.showDeleteDialog = true;
    },

    async deleteInventory() {
      this.deleting = true;
      try {
        const response = await deleteGoodsInventoryAPI(this.token, this.deleteId);
        if (!response.error) {
          this.showMessage(response.message || 'Inventory deleted', 'success');
          this.showDeleteDialog = false;
          this.deleteId = null;
          await this.loadInventories();
        } else {
          this.showMessage(response.message || 'Error deleting inventory', 'error');
        }
      } catch (err) {
        log("Error deleting inventory:", err);
        this.showMessage('Error deleting inventory', 'error');
      } finally {
        this.deleting = false;
      }
    },

    getStatusColor(statusName) {
      const colors = {
        'in_stock': 'success',
        'pending_shipment': 'warning',
        'in_transit': 'info',
        'delivered': 'primary'
      };
      return colors[statusName] || 'grey';
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    showMessage(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.showSnackbar = true;
    }
  },
  mounted() {
    this.loadData();
  }
});
</script>
